import { resolveGoogleNfcPreview } from '../../../../utils/googleNfc';
import type {
  PassItem,
  PassNfc,
  PassTemplateMeta,
} from '../../../../store/types/pass';
import type { NfcFeature, PreviewData } from '../../../../store/types/template';
import { getEffectiveTemplateImage } from '../../pass-templates-new/utils/imageHelpers';
import { extractFieldValue, resolveValue } from './fieldValueUtils';
import { mapPassTemplateImages, type TemplateImages } from './mapPassTemplateImages';

const API_TO_BUILDER_TYPE: Record<string, string> = {
  storeCard: 'loyalty',
  eventTicket: 'eventTicket',
  generic: 'generic',
  transit: 'transit',
  stampCard: 'stampCard',
};

const resolveFieldValue = resolveValue;

export interface TemplateNfcSettings {
  enabled?: boolean;
  source?: NfcFeature['source'];
  googleNfc?: boolean;
}

/** API returns pass nfc as a string value when enabled, not a PassNfc object. */
const resolvePassNfc = (pass: PassItem): NfcFeature => {
  const nfcRaw = pass.attributes.nfc;

  if (nfcRaw && typeof nfcRaw === 'object' && 'enabled' in nfcRaw) {
    const nfc = nfcRaw as PassNfc;
    return {
      enabled: nfc.enabled ?? false,
      source: nfc.source ?? 'uuid',
      customValue: nfc.customValue ?? '',
    };
  }

  if (typeof nfcRaw === 'string' && nfcRaw.length > 0) {
    const source: NfcFeature['source'] =
      nfcRaw === pass.attributes.extId
        ? 'extId'
        : nfcRaw === pass.id
          ? 'uuid'
          : 'custom';
    return {
      enabled: true,
      source,
      customValue: source === 'custom' ? nfcRaw : '',
    };
  }

  return {
    enabled: false,
    source: 'uuid',
    customValue: '',
  };
};

export const buildPassPreviewData = (
  pass: PassItem,
  template: PassTemplateMeta,
  templateImages?: TemplateImages,
  templateNfc?: TemplateNfcSettings
): PreviewData => {
  const passContent = pass.attributes.passContent;
  const passDesign = pass.attributes.passDesign;
  const passNfc = resolvePassNfc(pass);
  const isStampCard = template.template_type === 'stampCard';
  const images = mapPassTemplateImages(template, templateImages);
  const googleLogoImage =
    passDesign?.logoImageUrl || getEffectiveTemplateImage(images, 'google', 'logo');
  const googleWideLogoImage =
    template.chosen_google_logo === 'wide_logo'
      ? (getEffectiveTemplateImage(images, 'google', 'wideLogo') ||
         getEffectiveTemplateImage(images, 'shared', 'logo') || '')
      : '';

  const sharedWatermark = {
    color: templateImages?.watermark?.color || template.watermark?.color || '',
    position:
      templateImages?.watermark?.position || template.watermark?.position || '',
  };
  const appleWatermark = {
    color: isStampCard
      ? sharedWatermark.color
      : templateImages?.apple?.watermark?.color ||
        template.apple_banner_watermark?.color ||
        sharedWatermark.color,
    position: isStampCard
      ? sharedWatermark.position
      : templateImages?.apple?.watermark?.position ||
        template.apple_banner_watermark?.position ||
        sharedWatermark.position,
  };
  const googleWatermark = {
    color: isStampCard
      ? sharedWatermark.color
      : templateImages?.google?.watermark?.color ||
        template.google_banner_watermark?.color ||
        sharedWatermark.color,
    position: isStampCard
      ? sharedWatermark.position
      : templateImages?.google?.watermark?.position ||
        template.google_banner_watermark?.position ||
        sharedWatermark.position,
  };

  return {
    hideIdPlaceholder: true,
    templateName: template.name,
    passType:
      API_TO_BUILDER_TYPE[template.template_type] || template.template_type,
    transitType: template.transit_type,
    description: template.description,
    centralTitle: template.central_title,

    headerOneId: template.header.header_one.id,
    headerOneLabel: template.header.header_one.label,
    headerOneValue: isStampCard
      ? (resolveFieldValue(template.header.header_one, passContent) || '0')
      : resolveFieldValue(template.header.header_one, passContent),
    headerTwoId: template.header.header_two.id,
    headerTwoLabel: template.header.header_two.label,
    headerTwoValue: resolveFieldValue(template.header.header_two, passContent),
    headerThreeId: template.header.header_three.id,
    headerThreeLabel: template.header.header_three.label,
    headerThreeValue: resolveFieldValue(
      template.header.header_three,
      passContent
    ),

    primaryOneId: template.primary.primary_one.id,
    primaryOneLabel: template.primary.primary_one.label,
    primaryOneValue: resolveFieldValue(
      template.primary.primary_one,
      passContent
    ),
    primaryTwoId: template.primary.primary_two.id,
    primaryTwoLabel: template.primary.primary_two.label,
    primaryTwoValue: resolveFieldValue(
      template.primary.primary_two,
      passContent
    ),

    detailsOneId: template.secondary.sec_one.id,
    detailsOneLabel: template.secondary.sec_one.label,
    detailsOneValue: resolveFieldValue(template.secondary.sec_one, passContent),
    detailsTwoId: template.secondary.sec_two.id,
    detailsTwoLabel: template.secondary.sec_two.label,
    detailsTwoValue: resolveFieldValue(template.secondary.sec_two, passContent),
    detailsThreeId: template.secondary.sec_three.id,
    detailsThreeLabel: template.secondary.sec_three.label,
    detailsThreeValue: resolveFieldValue(
      template.secondary.sec_three,
      passContent
    ),
    detailsFourId: template.auxiliary.aux_one.id,
    detailsFourLabel: template.auxiliary.aux_one.label,
    detailsFourValue: resolveFieldValue(
      template.auxiliary.aux_one,
      passContent
    ),
    detailsFiveId: template.auxiliary.aux_two.id,
    detailsFiveLabel: template.auxiliary.aux_two.label,
    detailsFiveValue: resolveFieldValue(
      template.auxiliary.aux_two,
      passContent
    ),
    detailsSixId: template.auxiliary.aux_three.id,
    detailsSixLabel: template.auxiliary.aux_three.label,
    detailsSixValue: resolveFieldValue(
      template.auxiliary.aux_three,
      passContent
    ),

    backOneId: template.back_fields.back_one.id,
    backOneLabel: template.back_fields.back_one.label,
    backOneValue: resolveFieldValue(template.back_fields.back_one, passContent),
    backTwoId: template.back_fields.back_two.id,
    backTwoLabel: template.back_fields.back_two.label,
    backTwoValue: resolveFieldValue(template.back_fields.back_two, passContent),
    backThreeId: template.back_fields.back_three.id,
    backThreeLabel: template.back_fields.back_three.label,
    backThreeValue: resolveFieldValue(
      template.back_fields.back_three,
      passContent
    ),
    backFourId: template.back_fields.back_four.id,
    backFourLabel: template.back_fields.back_four.label,
    backFourValue: resolveFieldValue(
      template.back_fields.back_four,
      passContent
    ),
    backFiveId: template.back_fields.back_five.id,
    backFiveLabel: template.back_fields.back_five.label,
    backFiveValue: resolveFieldValue(
      template.back_fields.back_five,
      passContent
    ),

    logoImage:
      passDesign?.logoImageUrl || getEffectiveTemplateImage(images, 'shared', 'logo'),
    appleLogoImage:
      passDesign?.logoImageUrl || getEffectiveTemplateImage(images, 'apple', 'logo'),
    googleLogoImage,
    googleWideLogoImage,
    iconLogo: getEffectiveTemplateImage(images, 'shared', 'icon'),
    appleIconLogo: getEffectiveTemplateImage(images, 'apple', 'icon'),
    googleIconLogo: googleLogoImage,
    bannerImage:
      passDesign?.bannerImageUrl || getEffectiveTemplateImage(images, 'shared', 'banner'),
    appleBannerImage:
      passDesign?.bannerImageUrl || getEffectiveTemplateImage(images, 'apple', 'banner'),
    googleBannerImage:
      passDesign?.bannerImageUrl || getEffectiveTemplateImage(images, 'google', 'banner'),
    thumbnailImage:
      passDesign?.thumbnailImageUrl ||
      getEffectiveTemplateImage(images, 'apple', 'thumbnail'),

    passBackgroundColor:
      passDesign?.backgroundColor || template.background_color,
    titleFontColor: template.label_color,
    bodyFontColor: template.text_color,

    barcode: {
      enabled: pass.attributes.barcode?.enabled ?? false,
      type: pass.attributes.barcode?.type ?? null,
      source: pass.attributes.barcode?.source ?? null,
      value: pass.attributes.barcode?.value ?? '',
      displayText: pass.attributes.barcode?.displayText ?? false,
    },
    nfc: {
      ...passNfc,
      googleNfc: (template.template_type === 'loyalty' || template.template_type === 'storeCard' || template.template_type === 'stampCard')
        ? passNfc.enabled && (templateNfc?.googleNfc ?? true)
        : passNfc.enabled,
    },
    googleNfcEnabled: resolveGoogleNfcPreview(template.template_type, passNfc.enabled, templateNfc?.googleNfc),

    notificationHeader: '',
    appleAppLink: template.apple_app_link || [],
    googleAppLinkPlayStoreUrl: template.google_app_link.play_store_url,
    googleAppLinkPackageName: template.google_app_link.package_name,
    googleAppLinkWebAppUrl: template.google_app_link.web_app_url,
    googleAppLinkDisplayText: template.google_app_link.display_text,

    passDesign: passDesign
      ? {
          backgroundColor: passDesign.backgroundColor || '',
          logoImageUrl: passDesign.logoImageUrl || '',
          bannerImageUrl: passDesign.bannerImageUrl || '',
          thumbnailImageUrl: passDesign.thumbnailImageUrl || '',
          watermarkColor: sharedWatermark.color,
          watermarkPosition: sharedWatermark.position,
          appleBannerWatermarkColor: appleWatermark.color,
          appleBannerWatermarkPosition: appleWatermark.position,
          googleBannerWatermarkColor: googleWatermark.color,
          googleBannerWatermarkPosition: googleWatermark.position,
        }
      : {
          backgroundColor: '',
          logoImageUrl: '',
          bannerImageUrl: '',
          thumbnailImageUrl: '',
          watermarkColor: sharedWatermark.color,
          watermarkPosition: sharedWatermark.position,
          appleBannerWatermarkColor: appleWatermark.color,
          appleBannerWatermarkPosition: appleWatermark.position,
          googleBannerWatermarkColor: googleWatermark.color,
          googleBannerWatermarkPosition: googleWatermark.position,
        },

    watermarkColor: sharedWatermark.color,
    watermarkPosition: sharedWatermark.position,
    appleBannerWatermarkColor: appleWatermark.color,
    appleBannerWatermarkPosition: appleWatermark.position,
    googleBannerWatermarkColor: googleWatermark.color,
    googleBannerWatermarkPosition: googleWatermark.position,

    uuid: pass.id,
    displayBannerWatermarkPreview: true,
    displayGoogleBannerWatermarkPreview: true,
    focusedField: null,
    isFieldHighlighted: () => false,
    isStampBannerHighlighted: false,
  };
};
